'use client';

import './alert.scss';

import { Dispatch, type JSX, ReactNode, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { TrackError } from '@sola/cookies/components/datalayer.jsx';
import { DisplayPurposeProps } from '@sola/schema/dotcom/shared/display.js';

import { CloseButton } from '../../atoms/close-button/close-button';
import { LinkStyle } from '../../atoms/link-style/link-style';
import { cn } from '../../utils/helpers';

const AlertDismiss = (
  setDismissed: Dispatch<SetStateAction<boolean>>,
  callback?: () => void,
) => {
  setDismissed(true);

  if (callback) {
    callback();
  }
};

export interface AlertProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'title' | 'children'> {
  variant?: DisplayPurposeProps['variants'];
  title?: ReactNode;
  className?: string;
  message?: ReactNode;
  action?: JSX.Element;
  onClose?: () => void;
  noIcon?: boolean;
  dismissible?: boolean;
  closable?: boolean;
}

/**
 * Component for rendering alerts, which maintains its own dismissed state.
 */
export const Alert: React.FC<AlertProps> = ({
  variant = 'neutral',
  className,
  title,
  message,
  action,
  onClose,
  noIcon,
  dismissible = false,
  closable = false,
}) => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) {
    return;
  }

  return (
    <div
      className={cn(
        `Alert Alert--${variant} relative flex items-start justify-between`,
        className,
      )}
    >
      {variant === 'negative' && (
        <TrackError
          errorMessage={title ? title.toString() : message?.toString()}
        />
      )}
      <div
        className={cn('w-full p-comfy-md pr-snug-xl flex gap-comfy-xs', {
          Alert__wrapper: dismissible,
        })}
      >
        {!noIcon && (
          <div className="Alert__icon">
            {
              {
                neutral: <ArkIcon iconName="info-circle" />,
                positive: <ArkIcon iconName="check-circle" />,
                warning: <ArkIcon iconName="alert-triangle" />,
                negative: <ArkIcon iconName="alert-circle" />,
              }[variant]
            }
          </div>
        )}
        <div className="Alert__content shrink-1 grow-1">
          {title && (
            <div className="Alert__title label-md label-potent">{title}</div>
          )}
          {message && (
            <div className="Alert__message label-md label-moderate mt-snug-md">
              {message}
            </div>
          )}
          {dismissible && (
            <div className="Alert__btn-close">
              <LinkStyle purpose="neutral" noUnderline noPadding>
                <button
                  type="button"
                  onClick={() => AlertDismiss(setDismissed, onClose)}
                >
                  {t('core.actions.dismiss')}
                </button>
              </LinkStyle>
            </div>
          )}
          {action && (
            <div className="Alert__btn-action">
              <LinkStyle noUnderline noPadding>
                {action}
              </LinkStyle>
            </div>
          )}
        </div>
      </div>
      {(closable || dismissible) && (
        <CloseButton
          className="mt-snug-xl mr-snug-xl"
          onClick={() => AlertDismiss(setDismissed, onClose)}
          aria-label={t('core.alert.ariaLabel')}
        />
      )}
    </div>
  );
};
